import { nomeDeVariavel } from '@/shared/utils/nomeDeVariavel';

import type { BlocoLocal } from '../types/editor';

/**
 * Vínculo entre um "Iniciar prazo" solto e a espera que vigia o vencimento DELE (A-9).
 *
 * No despejo, Contestação e Purga correm juntas e a espera escuta `prazo_vencido`: sem o
 * vínculo, o vencimento do primeiro prazo retomava a espera do outro. O Iniciar prazo grava o
 * id do prazo numa variável (`savePrazoIdToVariable`) e a espera lê essa variável
 * (`prazoId: '{{variavel}}'`); o core só retoma com o `prazoId` do evento igual ao gravado.
 */
export const EVENTO_PRAZO_VENCIDO = 'arbitral.prazo_vencido.v1';

/**
 * Nome que o editor aceita nos dois campos. É higiene, não barreira de execução: o core resolve
 * qualquer `{{token}}` sem chaves nem espaços (`VARIABLE_TOKEN_REGEX` do `replaceVariables`), então
 * `prazo-b` funcionaria lá. Restringir a identificador mantém o nome igual ao das demais variáveis
 * do fluxo; por isso o problema entra como pendência do modal, que não trava a publicação — quem
 * reprova é o backend, e ele não tem regra para estes campos (D-07 do plano).
 */
export const NOME_DE_VARIAVEL_VALIDO = /^[A-Za-z_][A-Za-z0-9_]*$/;

type Parametros = BlocoLocal['parametros'];

export function vigiaPrazoVencido(p: Parametros): boolean {
  return Array.isArray(p.eventos) && p.eventos.includes(EVENTO_PRAZO_VENCIDO);
}

/**
 * Nome da variável num campo que pode vir da tela (`prazo_b`) ou do `node.data`
 * (`'{{prazo_b}}'`, no caminho sem `editorSource`): chaves e espaços somem, vazio vira `undefined`.
 *
 * @example variavelDoCampo('{{ prazo_b }}') // 'prazo_b'
 */
export function variavelDoCampo(valor: unknown): string | undefined {
  return typeof valor === 'string' ? nomeDeVariavel(valor) : undefined;
}

/**
 * A variável que o Aguardar evento vigia. Só vale com "Prazo vencido" entre os eventos: sem ele
 * o core ignora o campo, e emiti-lo faria o `node.data` dizer uma coisa que não acontece.
 *
 * @example prazoVigiadoDe({ eventos: [EVENTO_PRAZO_VENCIDO], prazoVigiado: 'prazo_b' }) // 'prazo_b'
 */
export function prazoVigiadoDe(p: Parametros): string | undefined {
  return vigiaPrazoVencido(p) ? variavelDoCampo(p.prazoVigiado) : undefined;
}

/**
 * A variável em que o Iniciar prazo grava o id. Só no modo solto (`pausar === false`): no modo
 * pausa o fluxo fica parado até o vencimento, ninguém espera por ele em paralelo e o core
 * ignora o campo.
 *
 * @example variavelGravadaPeloPrazo({ pausar: false, savePrazoIdToVariable: 'prazo_b' }) // 'prazo_b'
 */
export function variavelGravadaPeloPrazo(p: Parametros): string | undefined {
  return p.pausar === false ? variavelDoCampo(p.savePrazoIdToVariable) : undefined;
}
